import React, { useState, useEffect } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { googleLogin, createSession } from '../api';
import {
  WEB_CLIENT_ID,
  STORAGE_KEYS,
  ERROR_MESSAGES
} from '../constants/chatConstants';
import WhySignUpModal from './utils/WhySignUpModal';
import MessagePopup from './utils/MessagePopup';

const LoginForm = ({ onLoginSuccess, onNeedSignup }) => {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [isNative, setIsNative] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showWhySignUpModal, setShowWhySignUpModal] = useState(false);
  const [showMessagePopup, setShowMessagePopup] = useState(false);

  useEffect(() => {
    const checkPlatform = async () => {
      const nativePlatform = Capacitor.isNativePlatform?.() || false;
      setIsNative(nativePlatform);
    };
    checkPlatform();
  }, []);

  useEffect(() => {
    if (showMessagePopup && message && (message.includes('✅') || message.includes('successful'))) {
      const timer = setTimeout(() => {
        setShowMessagePopup(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showMessagePopup, message]);

  const showPopupMessage = (msg) => {
    setMessage(msg);
    setShowMessagePopup(true);
  };

  const handleGoogleAuth = async (credentialResponse) => {
    if (!credentialResponse?.credential) {
      setMessage('Google authentication failed. Please try again.');
      return;
    }

    setIsLoading(true);
    try {
      console.log('🔐 Logging in with Google...');
      const response = await googleLogin(credentialResponse.credential);

      if (response.data.success) {
        const userData = response.data.user;
        const authToken = response.data.token;

        console.log('✅ Login successful:', {
          email: userData.email,
          userId: userData.userId,
          hasToken: !!authToken
        });

        // ✅ CRITICAL: Save token first so interceptors can use it
        if (authToken) {
          localStorage.setItem('astroguru_token', authToken);
          console.log('🔑 Token saved to localStorage for API calls');
        }

        // ✅ Update auth context (login)
        await setUser(userData, authToken);

        // ✅ Create session – now Authorization header will be present
        try {
          const sessionResponse = await createSession(userData.userId);

          if (sessionResponse.data.success) {
            localStorage.setItem(
              STORAGE_KEYS.SESSION_ID,
              sessionResponse.data.session.sessionId
            );
            localStorage.setItem(STORAGE_KEYS.USER_ID, userData.userId);
            console.log('✅ Session created:', sessionResponse.data.session.sessionId);
          }
        } catch (sessionError) {
          console.error(' Session creation error:', sessionError);
        }

        showPopupMessage('✅ Login successful! Redirecting...');

        if (onLoginSuccess) {
          onLoginSuccess(userData);
        }

        // once setUser promise resolves, user state is up-to-date
        console.log('📍 Navigating to home after login, user state:', { userId: userData.userId });
        navigate('/home');
      }
    } catch (error) {
      console.error(' Login error:', error);

      if (error.response?.data) {
        const errorData = error.response.data;

        if (errorData.needsSignup) {
          setMessage('Account not found. Please sign up first.');
          if (onNeedSignup) {
            onNeedSignup();
          }
        } else if (errorData.waitingList) {
          setMessage(errorData.message || '⏳ Your account is on the waiting list.');
        } else {
          setMessage(errorData.message || 'Login failed');
        }
      } else {
        setMessage(ERROR_MESSAGES.NETWORK_ERROR);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNativeGoogleAuth = async () => {
    setIsLoading(true);
    setMessage('');

    try {
      console.log('📱 Initializing native Google Auth...');
      await GoogleAuth.initialize({
        clientId: WEB_CLIENT_ID,
        scopes: ['profile', 'email'],
        grantOfflineAccess: true
      });

      console.log('📱 Requesting Google sign-in...');
      const result = await GoogleAuth.signIn();

      const credential = result.authentication?.idToken || result.idToken;

      if (!credential) {
        throw new Error('No ID token received from Google Auth');
      }

      console.log('✅ Native Google Auth successful');
      await handleGoogleAuth({ credential });
    } catch (err) {
      console.error(' Native Google Auth Failed:', err);

      if (err.message?.includes('popup_closed_by_user') || err.message?.includes('SIGN_IN_CANCELLED')) {
        setMessage('⚠️ Sign-in was cancelled. Please try again.');
      } else if (err.message?.includes('NETWORK_ERROR')) {
        setMessage(' Network error. Please check your internet connection.');
      } else {
        setMessage(` Google authentication failed: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="">
        <div className="google-auth-container">
          {isNative ? (
            <button
              className="google-native-btn"
              onClick={handleNativeGoogleAuth}
              disabled={isLoading}
            >
              <span>🌐</span>
              {isLoading ? 'Signing in...' : 'Sign in with Google'}
            </button>
          ) : (
            <GoogleLogin
              onSuccess={handleGoogleAuth}
              onError={(error) => {
                console.error(' Web Google Auth Failed:', error);
                setMessage(' Google authentication failed. Please try again.');
              }}
              theme="filled_blue"
              size="large"
              text="signin_with"
              shape="rectangular"
              width="relative"
              disabled={isLoading}
            />
          )}
        </div>

        <button
          onClick={() => setShowWhySignUpModal(true)}
          className="preview-text"
        >
          <div className="google-auth-container">
            <div className="" style={{ minWidth: '200px' }}>
              Why Join Us?
            </div>
          </div>
        </button>

        <p className="auth-info-text">
          Secure login with Google • Continue your journey<br />
          🌟 Where Stars Meet Strategy with AI🌟
        </p>
      </div>

      {message && (
        <div
          className={`status-message ${
            message.includes('')
              ? 'error'
              : message.includes('⚠️')
              ? 'warning'
              : 'success'
          }`}
        >
          {message}
        </div>
      )}

      <WhySignUpModal
        showWhySignUpModal={showWhySignUpModal}
        setShowWhySignUpModal={setShowWhySignUpModal}
      />

      <MessagePopup
        showMessagePopup={showMessagePopup}
        message={message}
        setShowMessagePopup={setShowMessagePopup}
      />
    </>
  );
};

export default LoginForm;
